import {
  useIsMobile,
  WSButton,
  WSCheckboxToggle,
  WSElement,
  WSFlexBox,
  WSFormOld,
  WSIcon,
  WSLoader,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { sessionService } from "@services/sessionService";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { useCaptcha } from "../../components/Captcha";
import { useWSMutation } from "../../query/helpers";
import { validatorEmail } from "../../shared/validators/validatorEmail";
import { validatorPassword } from "../../shared/validators/validatorPassword";
import { WS_LINKS } from "../../types/wsLinks";
import { analyticsService } from "@services/analyticsService";
import { intercomService } from "@services/intercomService";
import { Divider } from "../components/Divider/Divider";
import { FooterNotes } from "../components/FooterNotes/FooterNotes";
import {
  GoogleAuthButton,
  GoogleAuthCredential
} from "../components/GoogleAuthButton/GoogleAuthButton";
import { useLoginHintRedirect } from "../hooks/useLoginHintRedirect";
import { Header } from "../components/Header/Header";
import { TextField } from "../components/TextField/TextField";
import styles from "./styles.module.scss";
import { CookieConsentBanner } from "../../modules/CookieConsent";

type RouteProps = RouteComponentProps;
type Props = RouteProps;

type SignInFormValues = {
  email: string;
  password: string;
  remember: boolean;
};

export const MemberSignIn: React.FC<Props> = () => {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [signInWithGoogleError, setSignInWithGoogleError] = useState("");
  const { isRedirecting: isLoginHintRedirecting } = useLoginHintRedirect();

  // v1 hands off migrated users here with their email in the URL once it has
  // already determined they're not an SSO org (see /v3/platform/sso in
  // V3_MIGRATION_TRACKER.md for why SSO users still stay on v1). Seeding
  // `email` from it renders straight into the password step on first paint —
  // the email-entry step never shows for these users.
  const prefillEmail = new URLSearchParams(location.search).get("email") ?? "";

  const [{ email, remember }, setFormData] = useState({
    email: prefillEmail,
    remember: true
  });

  useEffect(() => {
    if (prefillEmail) {
      history.replace(location.pathname);
    }

    // A session can also expire and land the user here without going through
    // the explicit sign-out flow, so shut down any still-booted Intercom
    // session on entry here too — otherwise the previous user's conversation
    // history stays visible on the login screen.
    intercomService.shutdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Email/password login is on the v3 session. The email step used to call
  // the v1 identify endpoint (/users/authentication) to route SSO users;
  // that endpoint is removed, so the step transition is now purely local.
  // SSO/migration routing for this screen happens upstream on v1 instead —
  // a migrated non-SSO user arrives here with `?email=` already set (above).
  const callWithCaptcha = useCaptcha("createSession");
  const [signIn, signInMeta] = useWSMutation<void, any, SignInFormValues>(
    values =>
      callWithCaptcha((captchaToken, captchaVersion, forceCaptcha) =>
        sessionService.login(values.email, values.password, values.remember, {
          captchaToken,
          captchaVersion,
          forceCaptcha
        })
      )
  );

  // Google sign-in is on the v3 session too. The v1 request carried a
  // `public-sign-up` flag; v3 has no equivalent, so the flag is dropped.
  // The button sits outside the form, so it cannot read the form's "Stay
  // signed in" value directly — the field's onChange mirrors it into
  // `remember`, which decides whether the refresh token persists.
  const [signInWithGoogle, signInWithGoogleMeta] = useWSMutation<
    void,
    any,
    GoogleAuthCredential
  >(async ({ idToken }) => {
    await sessionService.loginWithGoogle(idToken, remember);
    analyticsService.track("Log In with Google");
  });

  const onGoogleLoginSuccess = (credential: GoogleAuthCredential) => {
    signInWithGoogle(credential);
  };

  const onGoogleLoginFailure = (errorRes: any) => {
    setSignInWithGoogleError(errorRes.error);
  };

  if (isLoginHintRedirecting) {
    return (
      <>
        <BrowserPageTitle title="Sign In" />
        <WSLoader.Spinner size="L" my="6XL" />
      </>
    );
  }

  return (
    <>
      <CookieConsentBanner />
      <BrowserPageTitle title="Sign In" />

      <div className={styles.base}>
        <Header
          onBack={() => {
            if (email) {
              signInMeta.reset();
              setFormData({
                email: "",
                remember: true
              });
            } else {
              history.goBack();
            }
          }}
        />
        <main className={styles.main}>
          <div className={styles.headingWrapper}>
            <WSText kind={isMobile ? "Heading5" : "Heading1"}>Sign in</WSText>
          </div>
          <div className={styles.formWrapper}>
            <GoogleAuthButton
              onLoginSuccess={onGoogleLoginSuccess}
              onLoginFailure={onGoogleLoginFailure}
            >
              Sign in with Google
            </GoogleAuthButton>

            <Divider or />

            <WSFormOld
              defaultValues={{
                email,
                password: "",
                remember
              }}
              validationSchema={Yup.object().shape({
                email: validatorEmail.required("Email is required"),
                password: email
                  ? validatorPassword.required("Password is required")
                  : Yup.string(),
                remember: Yup.boolean()
              })}
              onSubmit={async (values: SignInFormValues) => {
                if (email) {
                  await signIn(values);
                } else {
                  setFormData({
                    email: values.email,
                    remember: values.remember
                  });
                }
              }}
            >
              <WSFormOld.Field
                mb="XL"
                name="email"
                label="Email Address"
                component={WSTextInput}
                hidden={!!email}
                componentProps={{
                  type: "email"
                }}
              />

              {email ? (
                <WSFlexBox.CenterY
                  py="M"
                  mb="XL"
                  className={styles.emailPreview}
                  wrap="nowrap"
                >
                  <WSFlexBox.Center mr="M" className={styles.userIcon}>
                    <WSIcon block name="user" color="gray400" />
                  </WSFlexBox.Center>

                  <WSElement className={styles.userDetails}>
                    <WSText singleLine>{email}</WSText>
                    <WSButton.Link
                      type="button"
                      onClick={() => {
                        signInMeta.reset();
                        setFormData({
                          email: "",
                          remember: true
                        });
                      }}
                    >
                      Not you?
                    </WSButton.Link>
                  </WSElement>
                </WSFlexBox.CenterY>
              ) : null}

              <WSFormOld.Field
                name="password"
                hidden={!email}
                component={TextField}
                componentProps={{
                  type: "password",
                  label: "Password",
                  labelLink: {
                    to: "/member/reset-password",
                    text: "Forgot password?",
                    testId: "resetPasswordLink"
                  }
                }}
              />

              <WSFormOld.Field
                my="XL"
                name="remember"
                component={WSCheckboxToggle}
                componentProps={{
                  label: "Stay signed in",
                  // Keeps `remember` in step with the toggle so the Google
                  // button, which renders outside this form, persists the
                  // session according to the user's current choice.
                  onChange: (value: boolean) =>
                    setFormData(prev => ({ ...prev, remember: value }))
                }}
              />

              <ErrorAlert
                my="XL"
                context="Auth"
                error={
                  signInMeta.error ||
                  signInWithGoogleMeta.error ||
                  signInWithGoogleError
                }
              />

              <WSFormOld.SubmitButton
                size="L"
                fullWidth
                data-testid="signInBtn"
                disabled={signInMeta.isLoading}
              >
                {email ? "Sign in" : "Next"}
              </WSFormOld.SubmitButton>
            </WSFormOld>

            <FooterNotes>
              By proceeding you agree to Wingspan's{" "}
              <a
                href={WS_LINKS.wingspanTosAcceptance}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>{" "}
              &{" "}
              <a
                href={WS_LINKS.wingspanPrivacyPolicyAcceptance}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              . This site is protected by reCAPTCHA and the Google{" "}
              <a
                href={WS_LINKS.googlePrivacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href={WS_LINKS.googleTermOfService}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{" "}
              apply.
            </FooterNotes>
          </div>
        </main>
      </div>
    </>
  );
};
